import React, { useState } from "react";
import api, { formatErr } from "../lib/api";
import { Sparkles, Save, Wand2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function AiReportPanel({ contextType, entityId, photoPaths, initialText = "", initialId = null }) {
  const [text, setText] = useState(initialText);
  const [reportId, setReportId] = useState(initialId);
  const [busy, setBusy] = useState(false);
  const [extra, setExtra] = useState("");
  const [editing, setEditing] = useState(false);

  if (!photoPaths?.length) {
    return (
      <div className="rounded-md border border-dashed border-brand-border bg-brand-surfaceAlt p-4 text-sm text-brand-muted">
        Upload at least one photo to generate an AI supported Faculty report.
      </div>
    );
  }

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/ai/photo-report", {
        photo_paths: photoPaths,
        context_type: contextType,
        entity_id: entityId,
        extra_context: extra,
      });
      setText(r.data.text);
      setReportId(r.data.id);
      setEditing(true);
      toast.success("AI report generated");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!reportId) return;
    setBusy(true);
    try {
      await api.put(`/ai/photo-report/${reportId}`, { text });
      setEditing(false);
      toast.success("Saved");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-brand-border bg-gradient-to-br from-[#F2F0EB] to-white p-5" data-testid="ai-report-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex items-center gap-2">
          <div className="h-9 w-9 rounded-md bg-brand-primary/10 grid place-items-center">
            <Sparkles className="h-4 w-4 text-brand-primary" />
          </div>
          <div>
            <div className="font-heading font-semibold">AI Field Report</div>
            <div className="text-xs text-brand-muted">Gemini 3 Flash · vision analysis</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {text && (
            <button onClick={() => setEditing(!editing)} className="h-9 px-3 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt text-sm font-medium" data-testid="ai-toggle-edit">
              {editing ? "Preview" : "Edit"}
            </button>
          )}
          <button onClick={generate} disabled={busy} className="inline-flex items-center gap-2 h-9 px-4 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white text-sm font-medium disabled:opacity-60" data-testid="ai-generate-btn">
            {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            {text ? "Regenerate" : "Generate"}
          </button>
        </div>
      </div>

      {!text && (
        <div className="mt-4">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-brand-muted">Extra context (optional)</label>
          <input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="e.g. 5 yr old, complaint of fever 2 days" className="mt-1 w-full h-11 px-3 rounded-md bg-white border border-brand-border focus:ring-2 focus:ring-brand-primary outline-none text-sm" data-testid="ai-extra" />
        </div>
      )}

      {text && (
        <div className="mt-4">
          {editing ? (
            <>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={10}
                className="w-full px-3 py-2 rounded-md bg-white border border-brand-border focus:ring-2 focus:ring-brand-primary outline-none text-sm font-mono leading-relaxed"
                data-testid="ai-textarea"
              />
              <div className="mt-3 flex justify-end">
                <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white text-sm font-medium disabled:opacity-60" data-testid="ai-save-btn">
                  <Save className="h-4 w-4" /> Save
                </button>
              </div>
            </>
          ) : (
            <div className="prose prose-sm max-w-none text-sm text-brand-text whitespace-pre-wrap leading-relaxed" data-testid="ai-rendered">{text}</div>
          )}
        </div>
      )}
    </div>
  );
}
